import Vue from "vue";
import axios from "axios";
import { eventBus } from "@/main";
import {
  FILE_LIMIT_EXCEED,
  FILE_STORAGE_PLAN_EXPIRED,
  REDIIVIEW_BASE_URL
} from "@/constants";

// What the server can produce a thumbnail from. Everything else stored as a
// media file (PDF, video, 3D models) keeps its own placeholder in the UI.
const THUMBNAILABLE_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp"
];

// Small enough that the first tiles appear quickly, and within the server's
// per-request cap.
const THUMBNAIL_CHUNK = 6;

// Every listing presigns fresh URLs, and a fresh URL is a browser cache miss.
// Upload polling refetches every few seconds and reopening a patient refetches
// too, so each would re-download every tile. Reusing the thumbnail URL we
// already handed out keeps the browser on its cached copy. Presigned URLs live
// 15 minutes; 10 leaves a tile's @error fallback as the only cost of the edge.
const THUMB_REUSE_MS = 10 * 60 * 1000;
// "No thumbnail" is remembered briefly so polling does not make the server
// retry a failed generation every few seconds.
const NO_THUMB_REUSE_MS = 2 * 60 * 1000;
const THUMB_CACHE_PRUNE_AT = 5000;

// id -> { url: string|null, at }
const thumbCache = new Map();
// id -> Promise<string|null>, so overlapping runs never ask for the same id twice
const inflightThumbs = new Map();
// Bumped when a list is replaced, so a run for the old list stops asking.
const listGeneration = { fileList: 0, fileTemplate: 0 };

function rememberThumb(id, url) {
  if (thumbCache.size > THUMB_CACHE_PRUNE_AT) {
    thumbCache.forEach((_, key) => recallThumb(key));
  }
  thumbCache.set(id, { url, at: Date.now() });
}

function recallThumb(id) {
  const hit = thumbCache.get(id);
  if (!hit) return undefined;
  const ttl = hit.url ? THUMB_REUSE_MS : NO_THUMB_REUSE_MS;
  if (Date.now() - hit.at < ttl) return hit;
  thumbCache.delete(id);
  return undefined;
}

/**
 * Prefers thumbnail URLs already handed out this session over the fresh ones
 * in a new listing, and records the fresh ones for next time.
 */
function adoptKnownThumbnails(files) {
  files.forEach(file => {
    if (!THUMBNAILABLE_TYPES.includes(file.fileType)) return;
    const known = recallThumb(file.id);
    if (known) {
      if (known.url) file.thumb = known.url;
      file.thumbResolved = true;
    } else if (file.thumb) {
      rememberThumb(file.id, file.thumb);
    }
  });
  return files;
}

const moduleMediaFile = {
  namespaced: true,
  state: {
    fileList: [],
    fileTemplate: [],
    fileDetail: {},
    newFilesLength: 0
  },
  mutations: {
    setFileList(state, data) {
      state.fileList = data;
    },
    concatFileList(state, data) {
      state.fileList = [...state.fileList, ...data];
    },
    // Writes to the file objects themselves rather than looking ids up in
    // fileList: lab screens and templates hold lists that fileList may no
    // longer contain, and those tiles must still leave the placeholder.
    applyThumbnails(state, entries) {
      entries.forEach(({ file, url }) => {
        if (url) file.thumb = url;
        file.thumbResolved = true;
      });
    },
    // A tile's thumbnail failed to load (expired presign, missing object):
    // fall back to the original and forget the URL so it is not reused.
    dropThumbnail(state, file) {
      thumbCache.delete(file.id);
      Vue.set(file, "thumb", null);
      Vue.set(file, "thumbResolved", true);
    },
    setFileTemplate(state, data) {
      state.fileTemplate = data;
    },
    concatFileTemplate(state, data) {
      state.fileTemplate = [...state.fileTemplate, ...data];
    },
    setFileDetail(state, data) {
      state.fileDetail = data;
    },
    RESET_FILE_LIST(state) {
      state.fileList = [];
    },
    setNewFileLength(state, data) {
      state.newFilesLength = data;
    }
  },
  actions: {
    async createMediaFiles({ commit, dispatch }, payload) {
      try {
        const urls = await dispatch("generatePresingUrl", payload);
        payload.urls = urls.map(i => i.url);
        const res = await dispatch("uploadFilesToS3", payload);
        return res;
      } catch (err) {
        const msg = err.response?.data.errorMessage;
        if (msg === "file limit exceeded")
          eventBus.$emit("alertSwal", FILE_LIMIT_EXCEED);
        if (msg === "file store plan expired.")
          eventBus.$emit("alertSwal", FILE_STORAGE_PLAN_EXPIRED);

        // Automatic error handling via interceptor
        throw err;
      }
    },
    async generatePresingUrl({ commit }, payload) {
      return await axios
        .post(
          `/api/mediaFile/getPresigned/${payload.clinicUrl}/${payload.branchUrl}`,
          payload.data
        )
        .then(res => {
          return res.data;
        })
        .catch(err => {
          // // Automatic error handling via interceptor
          throw err;
        });
    },
    async uploadFilesToS3({ commit }, { files, urls }) {
      const results = [];
      for (let i = 0; i < files.length; i++) {
        try {
          const result = await axios.put(urls[i], files[i], {
            transformRequest: (data, headers) => {
              delete headers["Authorization"];
              return data;
            },
            headers: {
              "Content-Type": files[i].type
            }
          });
          results.push(result);
        } catch (err) {
          results.push(null); // or any other error handling mechanism
          // Automatic error handling via interceptor
          throw err;
        }
      }
      return results;
    },
    async fecthMediaFiles({ commit, dispatch, getters }, payload) {
      const concat = payload.params.concat;
      delete payload.params.concat;

      const ephemeral = payload.params.ephemeral;
      delete payload.params.ephemeral;

      // Handle soft deleted parameter for recyclebin
      const softDeleted = payload.params.softDeleted;

      const res = await axios.get(
        `/api/mediaFile/${payload.clinicUrl}/${payload.branchUrl}`,
        { params: payload.params }
      );
      const data = adoptKnownThumbnails(
        getters.getMediaFileListFormat(res.data)
      );
      const { clinicUrl, branchUrl } = payload;
      if (ephemeral) {
        dispatch("resolveThumbnails", { clinicUrl, branchUrl, files: data });
        return data;
      }
      // A page appended by infinite scroll belongs to the current list; a
      // fresh fetch replaces it and retires the previous list's run.
      const generation = concat
        ? listGeneration.fileList
        : ++listGeneration.fileList;
      const isCurrent = () => listGeneration.fileList === generation;

      commit(concat ? "concatFileList" : "setFileList", data);
      dispatch("resolveThumbnails", {
        clinicUrl,
        branchUrl,
        files: data,
        isCurrent
      });
      return data;
    },

    /**
     * Fills in thumbnails for image files that do not have one yet.
     *
     * Deliberately not awaited by the caller: tiles show a placeholder until
     * their file is resolved (never the original, which would start a
     * multi-megabyte download the thumbnail was meant to avoid), then swap to
     * the thumbnail, or to the original when none can be made. Generating one
     * reads the original server-side, so this asks in small chunks.
     *
     * Every file handed in ends up with `thumbResolved` set, whatever happens,
     * so no tile is left on its placeholder.
     */
    async resolveThumbnails(
      { commit },
      { clinicUrl, branchUrl, files, isCurrent = () => true }
    ) {
      const pending = (files || []).filter(f => !f.thumbResolved);
      if (pending.length === 0) return;

      const settle = (list, urls = {}) =>
        commit(
          "applyThumbnails",
          list.map(file => ({ file, url: urls[file.id] || null }))
        );

      // Already being asked for by an earlier run (upload polling refetches
      // every few seconds): share its answer instead of asking again.
      const queue = [];
      pending.forEach(file => {
        const inflight = inflightThumbs.get(file.id);
        if (!inflight) return queue.push(file);
        inflight.then(url => settle([file], { [file.id]: url }));
      });

      for (let i = 0; i < queue.length; i += THUMBNAIL_CHUNK) {
        if (!isCurrent()) {
          // The list was replaced; nothing renders these objects any more.
          settle(queue.slice(i));
          return;
        }
        const chunk = queue.slice(i, i + THUMBNAIL_CHUNK);
        const ids = chunk.map(f => f.id);

        const request = axios
          .post(`/api/mediaFile/thumbnails/${clinicUrl}/${branchUrl}`, { ids })
          .then(res => (res.data && res.data.urls) || {});
        // Never rejects: a waiting run treats failure as "no thumbnail".
        ids.forEach(id =>
          inflightThumbs.set(
            id,
            request.then(urls => urls[id] || null, () => null)
          )
        );

        try {
          const urls = await request;
          // null means "no thumbnail" (not an image, missing, other clinic):
          // resolved, and the tile shows the original.
          ids.forEach(id => rememberThumb(id, urls[id] || null));
          settle(chunk, urls);
        } catch (error) {
          // Fall back to originals for everything left rather than leaving
          // tiles on the placeholder. Costs bandwidth, not function.
          settle(queue.slice(i));
          return;
        } finally {
          ids.forEach(id => inflightThumbs.delete(id));
        }
      }
    },
    async fecthMediaFilesTemplate({ commit, dispatch, getters }, payload) {
      const concat = payload.params?.concat;
      delete payload.params?.concat;

      const ephemeral = payload.params?.ephemeral;
      delete payload.params?.ephemeral;

      const res = await axios.get(
        `/api/mediaFile/${payload.clinicUrl}/${payload.branchUrl}`,
        {
          params: { category: "Template", ...payload.params }
        }
      );
      const data = adoptKnownThumbnails(
        getters.getMediaFileListFormat(res.data)
      );
      const { clinicUrl, branchUrl } = payload;
      if (ephemeral) {
        dispatch("resolveThumbnails", { clinicUrl, branchUrl, files: data });
        return data;
      }
      const generation = concat
        ? listGeneration.fileTemplate
        : ++listGeneration.fileTemplate;
      const isCurrent = () => listGeneration.fileTemplate === generation;

      // Templates are mostly PDFs, which are resolved up front; only image
      // templates cost a request.
      commit(concat ? "concatFileTemplate" : "setFileTemplate", data);
      dispatch("resolveThumbnails", {
        clinicUrl,
        branchUrl,
        files: data,
        isCurrent
      });
      return data;
    },
    async deleteMediaFile({ commit, state }, payload) {
      // Soft delete - move to recyclebin
      const res = await axios.delete(
        `/api/mediaFile/recycleBin/${payload.clinicUrl}/${payload.branchUrl}/${payload.id}`
      );
      if (res.status === 200) {
        const f = state.fileList.filter(i => {
          return payload.id !== i.id;
        });
        commit("setFileList", f);
        const t = state.fileTemplate.filter(i => {
          return payload.id !== i.id;
        });
        commit("setFileTemplate", t);
      }
      return res;
    },
    async restoreMediaFile({ commit, state }, payload) {
      const res = await axios.post(
        `/api/mediaFile/restore/${payload.clinicUrl}/${payload.branchUrl}/${payload.id}`
      );
      if (res.status === 200) {
        const f = state.fileList.filter(i => {
          return payload.id !== i.id;
        });
        commit("setFileList", f);
      }
      return res;
    },
    async permanentlyDeleteMediaFile({ commit, state }, payload) {
      // Using the regular delete endpoint for permanent deletion
      const res = await axios.delete(
        `/api/mediaFile/${payload.clinicUrl}/${payload.branchUrl}/${payload.id}`
      );
      if (res.status === 200) {
        const f = state.fileList.filter(i => {
          return payload.id !== i.id;
        });
        commit("setFileList", f);
      }
      return res;
    },
    async emptyRecycleBin({ commit, state }, payload) {
      // Update endpoint to match API path: /api/mediaFile/recycleBin/clear/{clinicURL}/{branchURL}
      const res = await axios.delete(
        `/api/mediaFile/clear/recycleBin/${payload.clinicUrl}/${payload.branchUrl}`
      );
      if (res.status === 200) {
        commit("setFileList", []);
      }
      return res;
    },
    async setNewFileLength({ commit, state }, payload) {
      commit("setNewFileLength", payload);
    },
    async addNewFileLength({ commit, state }, payload) {
      commit("setNewFileLength", payload);
    },
    async fetchRadiiViewUrl(context, { imageUrl }) {
      return await axios.post(
        REDIIVIEW_BASE_URL,
        { image_url: imageUrl },
        {
          withCredentials: false
        }
      );
    },
    async subscribeFileStorage({ commit }, payload) {
      const res = await axios.post(
        `/api/branch/${payload.clinicUrl}/${payload.branchUrl}/fileStorage/subscribe`,
        {
          durationMonths: payload.durationMonths,
          packageId: payload.packageId,
          confirmed: payload.confirmed !== undefined ? payload.confirmed : true
        }
      );
      return res;
    },
  },
  getters: {
    getFileList: state => {
      return state.fileList;
    },
    getFileTemplate: state => {
      return state.fileTemplate;
    },

    getMediaFileListFormat: (
      state,
      getters,
      rootState,
      rootGetters
    ) => data => {
      return data.map(i => {
        return {
          id: i.id,
          src: i.mediaURL,
          // Tiles render through MediaThumb (see its rules); lightboxes,
          // printing and document embedding always use `src`. Both fields are
          // declared up front so Vue 2 reacts when they are filled in later.
          thumb: i.thumbnailURL || null,
          thumbResolved:
            !!i.thumbnailURL || !THUMBNAILABLE_TYPES.includes(i.fileType),
          title: i.remark ? i.remark : i.category,
          category: i.category,
          mediaType: i.fileType === "application/pdf" ? "iframe" : null,
          remark: i.remark,
          creationDt: i.creationDt,
          treatmentId: i.treatmentId,
          fileSize: i.fileSize,
          fileType: i.fileType,
          deletedAt: i.deletedAt  // Include deletedAt field for recycle bin sorting
        };
      });
    }
  }
};
export default moduleMediaFile;
